// cSpell:ignore Berekening Forfait Gemeenschappelijke kosten maanden Privatieve Provisies
// The rows of the wizard's "Provisies" step per tenant (Lewis, 2026-10-16):
// one per PROVISION line of the lease, under the name the rental contract
// gives it ("Gemeenschappelijke kosten", "Privatieve kosten", or an extra
// line's own description). Forfait lines are no provision and get no row.
//
// The engine keeps settling per slice: the common costs in "common", the
// non-common costs plus every extra provision line in "private". A line's
// amount is a breakdown of its slice's amount, and the lines of a slice
// always add up to exactly what the engine bills for it.
//
// How a slice's amount is broken down: the provision history
// (GET /settlements/provision-schedule) is per slice only. The API folds the
// payment requests of the non-common costs and of the "other recurring"
// (extra) lines into the private slice, and indexations only track the
// common and non-common fields, so no line's own months can be told apart.
// A slice's amount is therefore spread over its lines by their monthly
// amounts today, in whole cents, the rounding remainder on the last line.
// The common slice has one line, so its row is exact.
//
// An amount typed in for a line: the engine reads one amount per slice
// (provisionExpectedOverrideBySlice), so the slice gets the sum of its lines,
// each line its typed amount or the amount it showed. The typed lines are
// remembered in the draft (provisionExpectedOverrideByLine) so a reload shows
// them as typed; the slice's other lines share what is left.

import { IProvisionLine } from "./contractProvision.utils";
import { fromCents, toCents } from "./money.utils";
import { ProvisionSlice } from "./provisionHistory";

// Two amounts within half a cent are the same amount.
const AMOUNT_TOLERANCE = 0.005;

// Split `totalCents` over `weights` proportionally, in whole cents: every
// part but the last rounded on its own, the last one the remainder, so the
// parts add up to exactly `totalCents` (negative totals too). Without any
// positive weight every part weighs the same.
export const splitByWeightCents = (params: {
  totalCents: number;
  weights: number[];
}): number[] => {
  const { totalCents, weights } = params;
  if (weights.length === 0) return [];
  const usable = weights.map(weight => Math.max(0, weight));
  const totalWeight = usable.reduce((sum, weight) => sum + weight, 0);
  const shares = totalWeight > 0 ? usable : usable.map(() => 1);
  const shareTotal = totalWeight > 0 ? totalWeight : usable.length;
  const parts = shares.map(share =>
    Math.round((totalCents * share) / shareTotal),
  );
  const last = parts.length - 1;
  parts[last] =
    totalCents - parts.slice(0, last).reduce((sum, part) => sum + part, 0);
  return parts;
};

// The lines of a lease that show under one slice: its provision lines with
// an amount; when none has one, all of them (the slice's amount then comes
// from what Rentio billed earlier), shared equally.
export const linesOfSlice = (params: {
  lines: IProvisionLine[];
  slice: ProvisionSlice;
}): IProvisionLine[] => {
  const ofSlice = params.lines.filter(line => line.slice === params.slice);
  const withAmount = ofSlice.filter(
    line => Math.abs(line.monthly) >= AMOUNT_TOLERANCE,
  );
  return withAmount.length > 0 ? withAmount : ofSlice;
};

// A slice's amount over its lines, keyed by line.
const shareOver = (params: {
  amount: number;
  lines: IProvisionLine[];
}): Record<string, number> => {
  const parts = splitByWeightCents({
    totalCents: toCents(params.amount),
    weights: params.lines.map(line => line.monthly),
  });
  return Object.fromEntries(
    params.lines.map((line, index) => [line.key, fromCents(parts[index])]),
  );
};

export interface IProvisionLineRow {
  // The line's key; `slice:<slice>` for a slice without a line on the lease.
  key: string;
  slice: ProvisionSlice;
  // Absent when the slice has no provision line on the lease (yet it counts
  // an amount: billed earlier, or typed in): the row then names the slice.
  line?: IProvisionLine;
  // The amount this row takes off the balance, and the line's share of the
  // slice's computed amount (what typing it in again restores).
  expected: number;
  computed: number;
  isTypedIn: boolean;
}

// The rows of one slice of a lease.
export const provisionLineRows = (params: {
  slice: ProvisionSlice;
  // Every provision line of the lease.
  lines: IProvisionLine[];
  // The slice's amount from the provision history, and the one the engine
  // bills (the slice's typed amount when there is one).
  computed: number;
  expected: number;
  sliceTypedIn: boolean;
  // The amounts typed in per line for this lease.
  typedByLine?: Record<string, number>;
}): IProvisionLineRow[] => {
  const { slice, computed, expected, sliceTypedIn } = params;
  const lines = linesOfSlice({ lines: params.lines, slice });
  if (lines.length === 0) {
    return [
      {
        key: `slice:${slice}`,
        slice,
        expected,
        computed,
        isTypedIn: sliceTypedIn,
      },
    ];
  }
  const computedShares = shareOver({ amount: computed, lines });
  const typed = Object.fromEntries(
    lines
      .filter(line => params.typedByLine?.[line.key] != null)
      .map(line => [line.key, params.typedByLine?.[line.key] ?? 0]),
  );
  const typedKeys = Object.keys(typed);
  const untyped = lines.filter(line => typed[line.key] == null);
  // What the typed lines leave of the slice's amount goes to the others.
  const restCents =
    toCents(expected) -
    typedKeys.reduce((sum, key) => sum + toCents(typed[key]), 0);
  const restShares = splitByWeightCents({
    totalCents: restCents,
    weights: untyped.map(line => line.monthly),
  });
  const amountCents: Record<string, number> = {
    ...Object.fromEntries(typedKeys.map(key => [key, toCents(typed[key])])),
    ...Object.fromEntries(
      untyped.map((line, index) => [line.key, restShares[index]]),
    ),
  };
  // Every line typed in and yet a rest (a line left the lease since): the
  // last line takes it, so the lines still add up to the slice.
  if (untyped.length === 0 && restCents !== 0) {
    const lastKey = lines[lines.length - 1].key;
    amountCents[lastKey] += restCents;
  }
  // A slice typed in as a whole (no line typed in) reads as typed in on
  // each of its lines.
  const wholeSliceTyped = sliceTypedIn && typedKeys.length === 0;
  return lines.map(line => ({
    key: line.key,
    slice,
    line,
    expected: fromCents(amountCents[line.key]),
    computed: computedShares[line.key],
    isTypedIn: typed[line.key] != null || wholeSliceTyped,
  }));
};

// What typing `amount` in for one line of a slice stores: the lease's typed
// lines, and the slice's amount for the engine (null = the computed one).
// Typing the line's computed share again, or emptying the field, clears it.
export const typeLineAmount = (params: {
  rows: IProvisionLineRow[];
  lineKey: string;
  amount: number | null;
  typedByLine?: Record<string, number>;
}): {
  typedByLine: Record<string, number>;
  sliceAmount: number | null;
} => {
  const { rows, lineKey, amount } = params;
  const row = rows.find(candidate => candidate.key === lineKey);
  const next = { ...params.typedByLine };
  if (
    amount == null ||
    !row ||
    Math.abs(amount - row.computed) < AMOUNT_TOLERANCE
  ) {
    delete next[lineKey];
  } else {
    next[lineKey] = fromCents(toCents(amount));
  }
  const sliceRows = rows.filter(candidate => candidate.line);
  const anyTyped = sliceRows.some(candidate => next[candidate.key] != null);
  if (!anyTyped) return { typedByLine: next, sliceAmount: null };
  // Each line: its typed amount, the computed share of the one just
  // cleared, or what it showed.
  const sliceCents = sliceRows.reduce((sum, candidate) => {
    const typedAmount = next[candidate.key];
    if (typedAmount != null) return sum + toCents(typedAmount);
    if (candidate.key === lineKey) return sum + toCents(candidate.computed);
    return sum + toCents(candidate.expected);
  }, 0);
  return { typedByLine: next, sliceAmount: fromCents(sliceCents) };
};

// How a row's amount was calculated, as the provisions step's "Berekening"
// column shows it: "€ 55,00 × 6 maanden" when the line's monthly amount
// today times the months gives its amount, else the average month ("gem.
// € 52,30 × 6 maanden": the history held other amounts, an indexation or a
// change of the lease). Undefined without a month to count.
export interface IProvisionCalculation {
  perMonth: number;
  months: number;
  isAverage: boolean;
}

export const provisionCalculation = (params: {
  // The amount computed from the provision history (never one typed in).
  computed: number;
  months: number;
  // The line's monthly amount on the lease today, when it has one.
  monthly?: number;
}): IProvisionCalculation | undefined => {
  const { computed, months, monthly } = params;
  if (!(months > 0)) return undefined;
  if (
    monthly != null &&
    Math.abs(Math.round(monthly * months * 100) - toCents(computed)) <= 1
  ) {
    return { perMonth: monthly, months, isAverage: false };
  }
  return {
    perMonth: fromCents(Math.round(toCents(computed) / months)),
    months,
    isAverage: true,
  };
};
